import { SECTOR_BUCKETS } from "./sectors";
import { normaliseToAlpha2 } from "./utils/country";
import {
  VULN_CLASS_BUCKETS,
  DEFAULT_VULN_CLASS_DIMENSION_VALUE,
} from "./vuln-classes";

/**
 * Compare dimension registry (KAN-237).
 *
 * The Analytics Compare API was originally hardcoded to a single `?sector=`
 * axis. This registry generalises that to a `?dimension=&value=` model so later
 * phases can plug in additional axes WITHOUT changing the existing sector
 * behaviour. In P1 the ONLY valid dimension is `sector`, and `?sector=` remains
 * a back-compatible alias for `?dimension=sector&value=`.
 *
 * P1 added `sector`. P2 (KAN-237) adds `jurisdiction`. P4 (KAN-237/KAN-233)
 * adds `country` (leak-site victim-claim population, ISO α2). P6 adds
 * `vuln-class` (CWE-derived vulnerability class).
 */

/** The default dimension when no `?dimension=` is supplied — preserves the
 *  pre-KAN-237 sector-only behaviour. */
export const DEFAULT_COMPARE_DIMENSION = "sector";

/** Valid `?dimension=` param values. P1: `sector`. P2: `jurisdiction`.
 *  P4: `country`. P6: `vuln-class`. */
export const COMPARE_DIMENSIONS: readonly string[] = [
  "sector",
  "jurisdiction",
  "country",
  "vuln-class",
];

/** Valid `?value=` param values for the `sector` dimension. Matches
 *  SECTOR_BUCKETS (including "other" and "all"). */
export const VALID_SECTOR_DIMENSION_VALUES: readonly string[] =
  SECTOR_BUCKETS.map((b) => b.value);

/** The default `?value=` for the `sector` dimension when none is supplied. */
export const DEFAULT_SECTOR_DIMENSION_VALUE = "all";

/**
 * The default `?value=` for the `jurisdiction` dimension when none is supplied
 * — mirrors sector's "all" (no jurisdiction filter; show every bucket).
 */
export const DEFAULT_JURISDICTION_DIMENSION_VALUE = "all";

/**
 * Max length of a jurisdiction `?value=` for P2. The DB holds raw, un-normalised
 * jurisdiction strings (no canonical geo slug yet — see KAN-234 #881), so the P2
 * validator only bounds length and rejects empty/whitespace rather than
 * restricting to geo-contract slugs.
 */
export const MAX_JURISDICTION_DIMENSION_VALUE_LENGTH = 100;

/**
 * True when `value` is a valid `jurisdiction` selector for P2.
 *
 * P2 contract (DECISION): match the selected value against the RAW
 * `articles.jurisdiction[]` strings as stored (case-insensitive exact match in
 * the service layer). So the validator accepts any non-empty, sanely-bounded
 * string, plus the `'all'` sentinel (no filter). Empty/whitespace is rejected
 * (→ 400, consistent with P1 malformed-param handling).
 *
 * TODO(KAN-234 #881): normalize DB jurisdiction values + the selected value via
 * normalizeJurisdictionLabel once #881 merges, so the value vocabulary becomes
 * canonical geo slugs and this validator can restrict to known slugs.
 */
export function isValidJurisdictionDimensionValue(value: string): boolean {
  if (value === DEFAULT_JURISDICTION_DIMENSION_VALUE) return true;
  const trimmed = value.trim();
  return trimmed.length > 0 && value.length <= MAX_JURISDICTION_DIMENSION_VALUE_LENGTH;
}

/**
 * The default `?value=` for the `country` dimension when none is supplied —
 * mirrors sector/jurisdiction "all" (no country filter; show every bucket).
 */
export const DEFAULT_COUNTRY_DIMENSION_VALUE = "all";

/**
 * Canonicalise a `country` dimension `?value=` to either the `'all'` sentinel
 * or an uppercase ISO-3166 α2 code; returns `null` for unrecognised input.
 *
 * P4 (DECISION): the country dimension reads `leak_site_claims.victim_country`,
 * which stores uppercase ISO α2 (KAN-233 shared geo-aggregation). So the value
 * vocabulary is ISO α2 (NOT the raw jurisdiction strings or geo slugs). Free-text
 * aliases like `usa`/`United Kingdom` are folded to their α2 via
 * {@link normaliseToAlpha2} so `?value=us`, `?value=US` and `?value=usa` all
 * canonicalise to `US`.
 */
export function normalizeCountryDimensionValue(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  if (trimmed.toLowerCase() === DEFAULT_COUNTRY_DIMENSION_VALUE) {
    return DEFAULT_COUNTRY_DIMENSION_VALUE;
  }
  return normaliseToAlpha2(trimmed);
}

/** True when `value` is a valid `country` selector for P4 (`'all'` or a
 *  resolvable ISO α2 code). */
export function isValidCountryDimensionValue(value: string): boolean {
  return normalizeCountryDimensionValue(value) !== null;
}

/** Valid `?value=` param values for the `vuln-class` dimension (KAN-237 P6):
 *  the VULN_CLASS_BUCKETS slugs (incl. `other` + `unclassified`) plus `all`. */
export const VALID_VULN_CLASS_DIMENSION_VALUES: readonly string[] = [
  DEFAULT_VULN_CLASS_DIMENSION_VALUE,
  ...VULN_CLASS_BUCKETS.map((b) => b.value),
];

/**
 * True when `value` is a valid `vuln-class` selector for P6. Unlike
 * jurisdiction (raw free-text), the vuln-class value vocabulary is a known
 * CLOSED set (the taxonomy slugs + `all`), so it is validated strictly like
 * sector.
 */
export function isValidVulnClassDimensionValue(value: string): boolean {
  return VALID_VULN_CLASS_DIMENSION_VALUES.includes(value);
}

/** True when `dimension` is a known/valid compare dimension. */
export function isValidCompareDimension(dimension: string): boolean {
  return COMPARE_DIMENSIONS.includes(dimension);
}

/**
 * True when `value` is valid for the given `dimension`.
 *
 * P1: the `sector` dimension's values are the SECTOR_BUCKETS values. P2 adds
 * `jurisdiction` (validated as a bounded non-empty string, see
 * {@link isValidJurisdictionDimensionValue}). Unknown dimensions always return
 * false (the route should already have rejected them via
 * {@link isValidCompareDimension}).
 */
export function isValidCompareDimensionValue(
  dimension: string,
  value: string,
): boolean {
  switch (dimension) {
    case "sector":
      return VALID_SECTOR_DIMENSION_VALUES.includes(value);
    case "jurisdiction":
      return isValidJurisdictionDimensionValue(value);
    case "country":
      return isValidCountryDimensionValue(value);
    case "vuln-class":
      return isValidVulnClassDimensionValue(value);
    default:
      return false;
  }
}
